import PropTypes from 'prop-types';
import React from 'react';
import PaymentOptionStyles from './PaymentOptions.styled';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import { Link } from 'react-router-dom';
import ArrowLink from '../../components/Shared/Link';
import Heading from '../../components/Heading/Heading';
import ContentSidebar from '../../components/ContentSidebar/ContentSidebar';
import { getPageContent } from '../../services/dpc';
import Content from '../../components/Content/Content';
import ButtonLink from '../../components/Shared/ButtonLink';
import Accordion from '../../components/Accordion/Accordion';
import { slugByLabel } from '../../util/faqCategories';
import ReactMarkdown from 'react-markdown';
import ContentChipHeading from '../../components/Shared/ContentChipHeading';
import CardCoupon from '../../components/Shared/CardCoupon';
import CouponSearch from '../../components/Shared/CouponSearch';
import Error from '../../components/Error/Error';
import useGTM from '../../hooks/useGTM';
import noop from '../../util/noop';
import createGTMClass from '../../util/createGTMClass';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';
import theme from '../../styles/theme';

const faqGTMLabels = {
  'ineligible-for-automated-phone-or-web-pay': 'ineligible_pay_method',
  'enroll-or-change-monthly-draft-info': 'enroll_change_monthly',
  'lost-payments': 'payment_lost',
};

const SomeThingsToKeepInMind = ({ className = '', onNumberClick = noop }) => (
  <ContentChipHeading
    className={className}
    heading='Some things to keep in mind'
  >
    <p>
      If you make payments online or use the automated phone payments system,
      here are a few things to know:
    </p>
    <ul>
      <li>
        Payments can be scheduled up to 14 days, as long as the future-dated
        payment does not cause the loan to become due for 2 payments.
      </li>
      <li>
        An online or automated payment will not be permitted if there are more
        than 2 payments currently due on the loan account. Please call{' '}
        <a
          href='tel:18009707250'
          onClick={onNumberClick}
          className={createGTMClass('phone', 'sidebar', 'text', ['underline'])}
        >
          1‑800‑970‑7250
        </a>{' '}
        to discuss payment.
      </li>
      <li>
        If the loan is due for 2 or more payments, amounts less than ¼ of one
        payment will not be allowed.
      </li>
      <li>
        After a{' '}
        <Link
          to='/resources/faqs/payment/ineligible-for-automated-phone-or-web-pay/'
          className='underline'
        >
          returned or failed payment
        </Link>
        , automated payment services will not be available for 90 days.
      </li>
    </ul>
    <p className='type-xs'>
      *If you are presently in bankruptcy or if your loan is past due, these
      payment options may not be available to you.
    </p>
  </ContentChipHeading>
);

function PaymentOptions({
  data: { titleTag, metaDescription, faqPod, contentCTAs } = {},
  error,
  isLoading = false,
  className,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const faqs =
    faqPod &&
    faqPod.map(
      ({
        faqQuestion: title,
        faqSummaryAnswer: answer,
        slug,
        faqCategory,
        ...rest
      }) => ({
        title,
        answer,
        showReadMore:
          rest.faqAnswerContent1 ||
          rest.faqAnswerContent2 ||
          rest.faqAnswerVideos,
        slug: slug.replace(
          'resources/faqs/',
          `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
        ),
        onExpand: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Payment Options',
          category: 'frequently_asked',
          action: 'summary',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace(/-/g, '_')
              .replace(/\/$/, ''),
        }),
        onReadMore: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Payment Options',
          category: 'frequently_asked',
          action: 'text',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace('-', '_')
              .replace(/\/$/, ''),
        }),
      })
    );

  return (
    <Layout isLoading={isLoading} className={className}>
      <SEO title={titleTag} description={metaDescription} />
      <Heading appearance='dark' background={theme.colors.blue10}>
        <h1>Payment Options</h1>
      </Heading>

      <ContentSidebar
        sidebarFirstOnMobile={true}
        sidebarWidth='391px'
        sidebar={
          <>
            <Content divider='left'>
              <h2 className='type-h5 color-vmfDarkBlue no-margin-bottom no-margin'>
                Have Questions?
              </h2>
              <p className='color-grey1 margin-top-1'>
                Call us at{' '}
                <a
                  href='tel:18009707250'
                  className={createGTMClass('phone', 'sidebar', 'text', [
                    'no-decoration color-grey1',
                  ])}
                  onClick={triggerGTMEvent({
                    name: 'vmf.cta_click',
                    location: 'Payment Options',
                    category: 'contact_us',
                    action: 'click_to_call',
                    label: 'call_payment_options',
                  })}
                >
                  1-800-970-7250
                </a>
              </p>
              <ButtonLink
                appearance='dark'
                outline
                to='/contact-us/'
                // className="display-inline-block"
                className={createGTMClass('contact', 'sidebar_cta', 'button', [
                  'display-inline-block',
                ])}
                onClick={triggerGTMEvent({
                  name: 'vmf.cta_click',
                  location: 'Payment Options',
                  category: 'contact_us',
                  action: 'button',
                  label: 'contact_us_1',
                })}
              >
                Contact us
              </ButtonLink>
            </Content>
            <SomeThingsToKeepInMind className='display-none-mobile display-none-tablet' />
          </>
        }
      >
        <Content divider={false} width='600px'>
          <h2 className='no-margin-bottom margin-top-12'>
            {contentCTAs?.[0]?.ctaHeader || 'Online Payment'}
          </h2>
          <p className='type-intro'>{contentCTAs?.[0]?.ctaSubHeader}</p>
          <ReactMarkdown
            source={contentCTAs?.[0]?.ctaIntro}
            renderers={{
              paragraph: ({ children }) => (
                <p className='type-caption color-grey1'>{children}</p>
              ),
              link: ({ href, children }) => (
                <a
                  href={href}
                  target='_blank'
                  rel='noreferrer noopener'
                  className={createGTMClass(
                    'create_account_op',
                    'paragraph',
                    'text',
                    ['underline']
                  )}
                  onClick={
                    href === '/portal/onboarding/'
                      ? triggerGTMEvent({
                          name: 'vmf.cta_click',
                          location: 'Payment Options',
                          category: 'create_account',
                          action: 'text',
                          label: 'create_account',
                        })
                      : noop
                  }
                >
                  {children}
                </a>
              ),
            }}
          />
          <section className='links margin-top-8'>
            <ButtonLink
              appearance='dark'
              to={contentCTAs?.[0]?.firstCTAButtonUrl}
              isExternal
              newTab
              className={createGTMClass(
                'create_account_op',
                'paragraph',
                'button'
              )}
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Payment Options',
                category: 'create_account',
                action: 'button',
                label: 'create_account',
              })}
            >
              {contentCTAs?.[0]?.firstCTAButtonText}
            </ButtonLink>
            <ButtonLink
              appearance='dark'
              to={contentCTAs?.[0]?.secondCTAButtonUrl}
              outline
              isExternal
              newTab
              className={createGTMClass('sign_in_op', 'paragraph', 'button')}
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Payment Options',
                category: 'account_sign_in',
                action: 'button',
                label: 'sign_in',
              })}
            >
              {contentCTAs?.[0]?.secondCTAButtonText}
            </ButtonLink>
          </section>
        </Content>

        <Content divider={false} width='600px'>
          <h2>Automatic Payment</h2>
          <p className='color-grey1'>
            You won’t have to worry about remembering to make a payment when you{' '}
            <Link
              to='/resources/faqs/payment/enroll-or-change-monthly-draft-info/'
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Payment Options',
                category: 'frequently_asked',
                action: 'text',
                label: 'auto_draft',
              })}
              className='underline'
            >
              enroll for Payment Auto Draft
            </Link>
            . It's easy and free. Vanderbilt Mortgage and Finance, Inc. will
            draft your mortgage payment from your checking or savings account
            when the payments are due. No need to write a check or send a
            payment. It's all done for you!
          </p>
        </Content>

        <SomeThingsToKeepInMind
          className='display-none-desktop'
          onNumberClick={triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Payment Options',
            category: 'contact_us',
            action: 'click_to_call',
            label: 'call_payment_options',
          })}
        />

        <Content divider={false} width='600px'>
          <h2>Pay by Phone</h2>
          <h3 className='type-h5 color-claytonBlue margin-bottom-2'>
            Automated Phone Pay:
          </h3>
          <p className='color-grey1 no-margin-top'>
            To pay by phone 24/7, all you need is your checkbook, loan number
            and the primary borrower's zip code. To use our FREE automated phone
            pay system, call{' '}
            <a
              href='tel:18009707250'
              className='no-decoration color-grey1'
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Payment Options',
                category: 'contact_us',
                action: 'click_to_call',
                label: 'call_payment_options',
              })}
            >
              <strong>1‑800‑970‑7250</strong>
            </a>
            .
          </p>

          <h3 className='type-h5 color-claytonBlue margin-top-8 margin-bottom-2'>
            Assisted Phone Pay:
          </h3>
          <p className='color-grey1 no-margin-top'>
            To pay by phone with assistance from one of our agents during normal
            business hours, all you need is your bank routing and account number
            and loan number. If you call us at{' '}
            <a
              href='tel:18009707250'
              className='no-decoration color-grey1'
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Payment Options',
                category: 'contact_us',
                action: 'click_to_call',
                label: 'call_payment_options',
              })}
            >
              <strong>1‑800‑970‑7250</strong>
            </a>{' '}
            between regular business hours of 8:30am-8:00pm Monday through
            Thursday ET and 8:30am-5:30pm on Friday ET, your payment will be
            posted the same day.
          </p>
        </Content>

        <Content divider={false} width='600px'>
          <h2>Pay by Check or Money Order</h2>
          <p>
            If you mail a check or money order for your monthly mortgage
            payment, please allow sufficient time for payments to reach us. When
            sending your payment, please remember to include your monthly
            payment coupon and write your loan number on your check or money
            order to ensure proper posting. Where legally allowed, a maximum fee
            of $35 will be charged for a returned check.
          </p>
          <h3 className='type-h5 color-claytonBlue margin-bottom-2 margin-top-8'>
            United States
          </h3>
          <p className='color-grey1 no-margin-top'>
            If you are mailing your payment from within the United States,
            select your state from the box below to determine the address your
            payment should be mailed to. Printable blank coupons are also
            provided for your convenience.
          </p>
          <ArrowLink
            to='/payment-options/Principal-Balance-Only-Coupons.pdf'
            newTab
            isExternal
          >
            Printable coupons to use when mailing money to be applied to your
            principal balance
          </ArrowLink>
          <CouponSearch />

          <h3 className='type-h5 color-claytonBlue margin-bottom-2 margin-top-8'>
            Military
          </h3>
          <p className='color-grey1 no-margin-top'>
            If you are a deployed member of the military using one of the
            following military ”states”, please mail your payments to the
            address below. Printable blank coupons are also provided for your
            convenience.
          </p>
          <p className='color-grey1 margin-bottom-8'>
            Military “States” include Armed Forces Africa, Armed Forces Canada,
            Armed Forces Europe, Armed Forces Middle East, Armed Forces Pacific
          </p>
          <CardCoupon url='/payment-options/Payment-Coupons.pdf'>
            <p>
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              PO Box 660180
              <br />
              Dallas, TX 75266-0180
            </p>
          </CardCoupon>
        </Content>

        <Content divider={false} className='margin-bottom-12' width='600px'>
          <h2>Pay by Money Transfer Service</h2>

          <h3 className='type-h5 margin-bottom-2'>
            MoneyGram<sup>®</sup>
          </h3>
          <p className='color-grey1 no-margin-top'>
            Use MoneyGram<sup>®</sup> ExpressPayment<sup>®</sup> service to
            make your Vanderbilt Mortgage payment at thousands of MoneyGram
            <sup>®</sup> agent locations nationwide including Walmart and CVS
            Pharmacy. MoneyGram<sup>®</sup> charges you a fee of $8.95 for this
            service. Use MoneyGram<sup>®</sup>{' '}
            <strong>Receive Code 2665</strong>.
          </p>
          <p>
            <a
              href='https://www.moneygram.com/mgo/us/en/'
              target='_blank'
              rel='noreferrer noopener'
              className='underline'
            >
              Find a MoneyGram<sup>®</sup> agent near you
            </a>{' '}
            or call{' '}
            <a href='tel:1800moneygram' className='color-grey1 no-decoration'>
              <strong>1-800-MONEYGRAM</strong>
            </a>
            .
          </p>
          <p className='type-xs'>
            MoneyGram and ExpressPayment are the registered marks of MoneyGram
            International, Inc.
          </p>

          <h3 className='type-h5 margin-bottom-2 margin-top-8'>
            Western Union<sup>®</sup>
          </h3>
          <p className='color-grey1 no-margin-top'>
            You can send a payment using{' '}
            <strong>
              Western Union Quick Collect<sup>®</sup>
            </strong>
            . When making a payment be sure to use the code city:{' '}
            <strong>SMOKE, TN</strong>. Western Union charges a fee of $12.95
            for this service.
          </p>
          <p className='color-grey1 no-margin-top'>
            <a
              href='https://www.westernunion.com/us/en/home.html'
              target='_blank'
              rel='noreferrer noopener'
              className='underline'
            >
              Find a Western Union agent near you.
            </a>
          </p>
          <p className='type-xs'>
            Western Union and Western Union Quick Collect are the registered
            marks of Western Union Holdings, Inc.
          </p>
        </Content>
      </ContentSidebar>

      <Accordion items={faqs} />

      <CTACardv2
        preHeaderContent={'Have Questions?'}
        headerContent={`We're Here to Help`}
        buttonText={'Contact Us'}
        buttonUrl={'/contact-us/'}
        gtmClassName={'contact'}
        ctaIntro={
          <>
            <p>
              Existing customers <a href='tel:1-800-970-7250'>1-800-970-7250</a>
            </p>
            <p>
              New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
            </p>
          </>
        }
      />
    </Layout>
  );
}

PaymentOptions.getInitialProps = async ({}) => {
  try {
    const dpcData = await getPageContent('/resources/payment-options/');

    if (dpcData.status !== 200) {
      throw dpcData.statusText;
    }
    return { data: dpcData.data.result };
  } catch (error) {
    // Handle issues loading data
    if (error.response.status === 404) return { statusCode: 404 };
    return { error };
  }
};

PaymentOptions.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default PaymentOptionStyles(PaymentOptions);
